import { useLayoutEffect, useRef } from 'react';
import { formatUnitPrice } from '~/utils';
import { prefersReducedMotion } from './sellerComparisonAnimations';

const PRICE_COUNT_UP_DURATION_MS = 450;

interface AnimatedPriceProps {
  amount: number;
  currencyCode: string;
}

// Tweens the displayed price from its previous value to the new one with a
// short count-up, and flashes the number, so a buyer can see exactly what
// changed when they (or a negotiation) update a seller's price. The count-up
// is driven imperatively against the span so it never re-renders per frame.
export const AnimatedPrice = ({ amount, currencyCode }: AnimatedPriceProps) => {
  const priceSpanRef = useRef<HTMLSpanElement>(null);
  const displayAmountRef = useRef(amount);
  const animationFrameRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const priceSpan = priceSpanRef.current;
    if (!priceSpan) return;

    const startAmount = displayAmountRef.current;
    const endAmount = amount;

    // No animation needed: snap straight to the new value.
    if (prefersReducedMotion() || startAmount === endAmount) {
      displayAmountRef.current = endAmount;
      priceSpan.textContent = formatUnitPrice(endAmount, currencyCode);
      return;
    }

    // Restart the flash highlight from the beginning.
    priceSpan.classList.remove('price-flash');
    void priceSpan.offsetWidth;
    priceSpan.classList.add('price-flash');

    // Paint the prior amount synchronously (this runs before the browser's
    // first paint) so the count-up always begins from where the number was.
    // React has already committed the final {amount} into the span, so without
    // this the first paint flashes the final value and the first animation
    // frame jumps backward to the start.
    priceSpan.textContent = formatUnitPrice(startAmount, currencyCode);

    let startTimestamp: number | null = null;
    const animate = (timestamp: number) => {
      if (startTimestamp === null) startTimestamp = timestamp;
      const progress = Math.min(
        (timestamp - startTimestamp) / PRICE_COUNT_UP_DURATION_MS,
        1
      );
      // Ease-out cubic: fast at first, settling gently onto the final value.
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentAmount =
        startAmount + (endAmount - startAmount) * easedProgress;
      displayAmountRef.current = currentAmount;
      priceSpan.textContent = formatUnitPrice(currentAmount, currencyCode);
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [amount, currencyCode]);

  return (
    <span ref={priceSpanRef}>{formatUnitPrice(amount, currencyCode)}</span>
  );
};
